import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { take, tap, timer } from 'rxjs';

import { OrgStatus } from '@efficiently/models';

import { TestIdDirective } from '@efficiently/client-cdk-test-id';
import { EffIconComponent } from '@efficiently/client-ui-icons';
import { formatDuration } from '@efficiently/client-util-date-time';
import { SnackbarService } from '@efficiently/client-widget-snackbar';

import { ChargeBeePortalService } from '@ds/core/services/charge-bee';
import { ChargebeeGqlService } from '@ds/shared/graphql/chargebee/chargebee.service';

const PAGE_RELOAD_TIME = 5000;

type SubscriptionRenewalDialogData = {
	isLicensed: boolean;
	seconds: number;
	status: string;
};

@Component({
	selector: 'ds-subscription-renewal',
	templateUrl: './subscription-renewal.component.html',
	styleUrls: ['./subscription-renewal.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [EffIconComponent, MatDialogContent, MatDialogActions, MatButton, TestIdDirective],
})
export class SubscriptionRenewalComponent {
	private readonly snackbarService = inject(SnackbarService);
	private readonly chargeBeePortalService = inject(ChargeBeePortalService);
	private readonly dialogRef = inject(MatDialogRef<SubscriptionRenewalComponent>);
	private readonly chargebeeGqlService = inject(ChargebeeGqlService);
	private readonly data = inject<SubscriptionRenewalDialogData>(MAT_DIALOG_DATA);

	private status: string = '';

	protected duration: string = '';
	protected isLicensed: boolean = true;

	constructor() {
		this.duration = '';
		if (this.data?.seconds > 0) {
			this.duration = `in ${formatDuration({ seconds: this.data.seconds }, 'durationHumanized')}`;
		}
		this.isLicensed = this.data?.isLicensed;
		this.status = this.data?.status;
	}

	public handleContinue(): void {
		this.chargebeeGqlService
			.getSubscriptionState()
			.pipe(tap(() => this.dialogRef.close()))
			.subscribe();
	}

	public handleOpenBilling(): void {
		this.chargeBeePortalService
			.openBilling((isAllowReload: boolean) => {
				if (isAllowReload) {
					if (this.status === OrgStatus.Trial || this.status === OrgStatus.Expired) {
						timer(PAGE_RELOAD_TIME).subscribe(() => window.location.reload());
					}
				} else {
					this.snackbarService.processing(
						'Please wait subscription apply processing... It may take a few minutes. Thank you for your patience',
						{
							duration: 10000,
						},
					);
				}
			})
			.pipe(take(1))
			.subscribe();
		this.dialogRef.close();
	}
}
